/**
 * 进一步封装滚动加载组件
 *
 * 目前用在: 首页搜索结果、合集作品广场详情页
 *
 * @example
 * pages/index.tsx
 */

import { ApolloQueryResult, DocumentNode } from '@apollo/client'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { InfiniteScroll } from '~/components/business/InfiniteScroll'
import { PAGE_SIZE } from '~/configs'
import { uniqueArray, uniqueArrayUnShift } from '~/utils'
import { useImperativeQuery } from './useImperativeQuery'

interface Params<T, TData, TVariables> {
  /** gql query document */
  queryDoc: DocumentNode
  /** 除了offset, first外，额外的query参数 */
  extraQueryParams?: Partial<TVariables>
  /** 初始化数据，小于 pageSize 且 hasNextPage 时立即调用一次 onScroll */
  initData: T[]
  /** 翻页数量，搜索页面布局问题，使用3和4的倍数 */
  initPageSize?: number
  /** 是否有下一页，不确定的时候填true */
  hasNextPage: boolean
  /** 用于从ApolloQueryResult<TData>取出hasNextPage字段 */
  hasNextPageExtractor: (arg: ApolloQueryResult<TData>) => boolean
  /** 用于从ApolloQueryResult<TData>取出endCursor字段 */
  endCursorExtractor: (arg: ApolloQueryResult<TData>) => string
  /** 用于从ApolloQueryResult<TData>取出data字段, 合并进list */
  dataExtractor: (arg: ApolloQueryResult<TData>) => T[]
  /** 数据合并去重字段，不传默认id, 透传到uniqueArray */
  dataUniqueBy?: (it: any) => any
  onFetchComplete?: (dataList?: T[], direction?: string, params?: any) => void
  /** 游标最后一项数据 */
  endCursor: string
  startCursor?: string
  hasPreviousPage?: boolean
  hasPrevPageExtractor?: (arg: ApolloQueryResult<TData>) => boolean
  startCursorExtractor?: (arg: ApolloQueryResult<TData>) => string
}

export default function useInfiniteScrollCursor<T, TData, TVariables>({
  queryDoc,
  extraQueryParams,
  initData,
  hasNextPage: _hasNextPage,
  hasPreviousPage: _hasPreviousPage,
  initPageSize = PAGE_SIZE,
  onFetchComplete,
  hasNextPageExtractor,
  endCursorExtractor,
  dataExtractor,
  dataUniqueBy,
  endCursor,
  startCursor,
  startCursorExtractor,
  hasPrevPageExtractor,
}: Params<T, TData, TVariables>) {
  const [list, setList] = useState(initData)
  const [loading, setLoading] = useState(false)
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(_hasNextPage)
  const [hasPrevPage, setHasPrevPage] = useState(_hasPreviousPage)
  const [pageInfo, setPageInfo] = useState({
    pageSize: initPageSize,
    endCursor: endCursor,
    startCursor: startCursor,
  })

  const queryFunc = useImperativeQuery<TData, TVariables>(queryDoc)

  useEffect(() => {
    /** 页面中initData改变了，一般由页面tab切换触发 */
    setList(initData)
    setLoading(false)
    setLoadingPrev(false)
    setHasNextPage(_hasNextPage)
    setHasPrevPage(_hasPreviousPage)
    setPageInfo({
      endCursor: endCursor,
      pageSize: initPageSize,
      startCursor: startCursor,
    })
  }, [initData])

  const onScroll = useCallback(
    (params?: any) => {
      // fix: 第一次进入的时候 onScroll 触发两次
      if (loading) {
        return
      }
      setLoading(true)
      queryFunc({
        endCursor: pageInfo.endCursor,
        first: pageInfo.pageSize,
        ...extraQueryParams,
      } as unknown as TVariables)
        .then(data => {
          setPageInfo(() => {
            return {
              ...pageInfo,
              endCursor: endCursorExtractor(data),
            }
          })

          setHasNextPage(hasNextPageExtractor(data) || false)

          setList(list => {
            const _list = uniqueArray(list, dataExtractor(data), dataUniqueBy)
            onFetchComplete?.(_list, 'next', params)
            return _list
          })
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [queryFunc, pageInfo, initData, loading]
  )

  const onScrollPre = useCallback(
    (params?: any) => {
      if (loadingPrev) {
        return
      }
      setLoadingPrev(true)
      queryFunc({
        startCursor: pageInfo.startCursor,
        last: pageInfo.pageSize,
        ...extraQueryParams,
      } as unknown as TVariables)
        .then(data => {
          setPageInfo(() => {
            return {
              ...pageInfo,
              startCursor: startCursorExtractor?.(data),
            }
          })

          setHasPrevPage(hasPrevPageExtractor?.(data) || false)
          setList(list => {
            const dataList = dataExtractor(data)
            const _list = uniqueArrayUnShift(list, dataList, dataUniqueBy)
            onFetchComplete?.(_list, 'prev', params)
            return _list
          })
        })
        .finally(() => {
          setLoadingPrev(false)
        })
    },
    [queryFunc, pageInfo, initData]
  )

  useEffect(() => {
    if (Math.floor(initData.length / initPageSize) === 0 && hasNextPage) {
      onScroll()
    }
  }, [initData, hasNextPage])
  const InfiniteScollComp = useMemo(
    () => (
      <InfiniteScroll
        onScroll={onScroll}
        hasNextPage={hasNextPage}
        loading={hasNextPage || loading}
      />
    ),
    [onScroll, hasNextPage, loading]
  )

  const InfiniteScollPrevComp = useMemo(
    () => (
      <InfiniteScroll
        onScroll={onScrollPre}
        hasNextPage={hasPrevPage || false}
        loading={loadingPrev}
      />
    ),
    [onScrollPre, hasPrevPage, loadingPrev]
  )

  return {
    /** 封装后的 InfiniteScoll */
    InfiniteScollComp,
    /** 合并后的数据列表，一般用不到 */
    list,
    /** 是否有下一页，一般用不到 */
    hasNextPage,
    hasPrevPage,
    onScroll,
    onScrollPre,
    /** 暴露出来，用于手动请求下一页，用于点击『加载更多』按钮 */
    InfiniteScollPrevComp,
    loading,
    loadingPrev,
  }
}
